import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { LogOut, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandLogo } from "@/components/BrandLogo";
import { MobileNav } from "@/components/MobileNav";

export function AppHeader({ right }: { right?: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { to: "/tapes", label: "Tapes" },
    { to: "/auditions", label: "Auditions" },
    { to: "/calendar", label: "Calendar" },
    { to: "/snapshots", label: "Snapshots" },
  ] as const;

  return (
    <header className="sm:border-b border-border bg-background/70 supports-[backdrop-filter]:bg-background/55 backdrop-blur-xl sticky top-0 z-20 max-sm:pb-1 max-sm:after:content-[''] max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-full max-sm:after:h-10 max-sm:after:pointer-events-none max-sm:after:bg-gradient-to-b max-sm:after:from-background/80 max-sm:after:via-background/30 max-sm:after:to-transparent">
      <div className="container max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <Link to="/tapes" className="flex items-center gap-2">
            <BrandLogo className="h-8 w-auto" />
            <span className="font-semibold text-[15px] tracking-tight">
              RoleBoard
            </span>
          </Link>
          <nav className="hidden sm:flex items-center gap-1">
            {navItems.map((item) => {
              const active = location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  search={{} as never}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    active
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {right}
          <ThemeToggle />
          {user && (
            <div className="flex items-center gap-2">
              <span className="hidden md:inline text-xs text-muted-foreground max-w-[180px] truncate">
                {user.email}
              </span>
              <Link
                to="/settings"
                className="inline-flex items-center justify-center size-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label="Settings"
              >
                <SettingsIcon className="size-4" />
              </Link>
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  await signOut();
                  navigate({ to: "/login" });
                }}
              >
                <LogOut className="size-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
      <MobileNav />
    </header>
  );
}
